import {
  Bolton,
  ContactPreferenceValues,
  ContactPreferenceValuesV2,
  OrderCredit,
  Plan,
} from '@smarty-nx/data'
import {
  getDiscountValue,
  getFamilyPlanPrice,
  getOfferName,
  getPlanCategory,
  getV1PlanCategory,
  getV3PlansCategoryName,
  returnCalculatedGross,
  returnCalculatedTax,
  returnInputAsBoolean,
  returnV2CalculatedTax,
} from '@smarty-nx/code-utils'

import {
  DATA_LAYER_JOURNEY_TYPES,
  ORDER_TYPES,
  PORT_IN_STATUS,
} from '../constants'
import { buildUser } from '../services'
import {
  AddonProductObject,
  AddonTransactionData,
  PaymentData,
  ProductObject,
  TransactionData,
  UserObject,
} from '../types'

/**
 * Builds cart data from the V2 plan attributes, a checkout step and a checkout type
 * */
export const buildCartData = ({
  plan,
  checkoutStep,
  orderType,
  checkoutType,
  membersNumber,
  isUlfp = false,
  numberOfSims,
}: {
  plan: Plan
  checkoutStep: number
  orderType?: ORDER_TYPES | string
  checkoutType?: DATA_LAYER_JOURNEY_TYPES
  membersNumber?: number
  isUlfp?: boolean
  numberOfSims?: number
}) => {
  const total =
    membersNumber && membersNumber > 1 && plan?.additionalSims
      ? getFamilyPlanPrice(plan, membersNumber)
      : plan?.finalPriceWithoutCredit?.value

  if (plan) {
    return {
      checkoutStep: checkoutStep,
      ...(checkoutType && {
        checkoutType,
      }),
      ...(orderType && {
        order_type: orderType,
      }),
      ...(numberOfSims && { number_of_sims: numberOfSims }),
      currency: 'GBP',
      lines: [
        {
          product: buildProductData(
            plan,
            membersNumber,
            orderType,
            isUlfp
          ) as ProductObject,
        },
      ],
      value: {
        chargeType: 'monthly',
        displayGross: total,
        displayTax: returnV2CalculatedTax(total),
      },
    }
  }
  return null
}

/**
 * Builds cart data from the V2 plan attributes
 * */
export const buildProductData = (
  plan: Plan,
  membersNumber?: number,
  orderType?: ORDER_TYPES | string,
  isULFP = false
): ProductObject | AddonProductObject | null => {
  const total =
    membersNumber && membersNumber > 1 && plan?.additionalSims
      ? getFamilyPlanPrice(plan, membersNumber)
      : plan?.finalPriceWithoutCredit?.value

  if (plan) {
    return {
      id: plan.slug,
      name: plan.slug,
      category: isULFP ? getV3PlansCategoryName(plan) : getPlanCategory(plan),
      currency: plan.finalPrice.currency,
      quantity: 1,
      value: {
        chargeType: 'monthly',
        displayGross: total,
        displayTax: returnV2CalculatedTax(total),
      },
      // this part is absent in contract of data to be sent
      ...(!isULFP && {
        discount: {
          coupon: getOfferName(plan),
          value: { displayGross: getDiscountValue(plan) },
        },
      }),
      ...(orderType && {
        order_type: orderType,
      }),
    }
  }
  return null
}

/**
 * Builds addon data from the V2 add-on attributes
 * */
export const buildAddonProductData = (addon: Bolton) => {
  const { id, name, category, price_to_be_paid } = addon
  return {
    id,
    name,
    category,
    quantity: 1,
    value: {
      chargeType: 'once',
      displayGross: returnCalculatedGross(price_to_be_paid),
      displayTax: returnCalculatedTax(price_to_be_paid),
    },
  }
}

/**
 * Builds product data from the v2 plan attributes
 * */
export const buildV2ProductData = ({
  plan,
  isPreloadedSim,
  isUlfp = false,
  total,
}: {
  plan: Plan
  isPreloadedSim?: boolean
  isUlfp?: boolean
  total: number
}) => {
  if (plan) {
    return {
      id: plan?.slug,
      name: plan?.slug, // DMPG team wants to see "fg-unlimited" f.e. as name
      category: isUlfp
        ? getV3PlansCategoryName(plan)
        : getV1PlanCategory(plan, isPreloadedSim),
      value: {
        chargeType: 'monthly',
        displayGross: total,
        displayTax: returnV2CalculatedTax(total),
      },
      discount: {
        coupon: getOfferName(plan),
        value: {
          displayGross: getDiscountValue(plan),
        },
      },
    }
  }
  return null
}

/**
 * Builds a user object and populates it with contact preferences data
 * Uses the same format as the contact preferences form:
 * email_marketing / sms_marketing / profiled_marketing / third_party_marketing
 */

export const buildPrefsString = (preferencesData: ContactPreferenceValues) => {
  if (preferencesData.push_notification_marketing !== undefined) {
    return `email_marketing:${returnInputAsBoolean(
      preferencesData.email_marketing
    )} | sms_marketing:${returnInputAsBoolean(
      preferencesData.sms_marketing
    )} | push_notification_marketing:${returnInputAsBoolean(
      preferencesData.push_notification_marketing
    )} | profiled_marketing:${returnInputAsBoolean(
      preferencesData.profiled_marketing
    )} | third_party_marketing:${returnInputAsBoolean(
      preferencesData.third_party_marketing
    )}`
  }
  return `email_marketing:${returnInputAsBoolean(
    preferencesData.email_marketing
  )} | sms_marketing:${returnInputAsBoolean(
    preferencesData.sms_marketing
  )} | profiled_marketing:${returnInputAsBoolean(
    preferencesData.profiled_marketing
  )} | third_party_marketing:${returnInputAsBoolean(
    preferencesData.third_party_marketing
  )}`
}

/**
 * Builds user.customer with marketing preferences data
 * Data is provided from form inputs on the contact preferences page
 * and all account creation journeys (web order, PayPal express,
 * token creation)
 * */
export const buildUserWithPrefs = (
  preferencesData: ContactPreferenceValues
) => {
  if (preferencesData) {
    return {
      customer: {
        marketing_preferences: buildPrefsString(preferencesData),
      },
    }
  }
  return null
}

export const buildUserWithPrefsV2 = (
  preferencesData: ContactPreferenceValuesV2
) => {
  if (preferencesData) {
    return {
      customer: {
        marketing_preferences: buildMarketingPrefsString(preferencesData),
      },
    }
  }
  return null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const remapMarketingPrefs = (prefs: { [key: string]: any }) => {
  const { third_party_marketing, ...rest } = prefs
  const partner_marketing = Boolean(third_party_marketing)
  return {
    ...rest,
    ...(typeof third_party_marketing !== 'undefined' && {
      partner_marketing,
    }),
  }
}

export const buildPaymentData = (orderCredit: OrderCredit): PaymentData[] => {
  const { payment_method, amount_paid, credit_used } = orderCredit
  const paymentData: PaymentData[] = []

  if (amount_paid || payment_method) {
    paymentData.push({
      type: payment_method,
      value: {
        displayGross: amount_paid,
      },
    })
  }

  if (credit_used) {
    paymentData.push({
      type: 'credit',
      value: {
        displayGross: credit_used,
      },
    })
  }

  return paymentData
}

export const buildTransactionData = ({
  id,
  orderCredit,
  plan,
  checkoutStep,
  checkoutType,
  portInOption,
  membersNumber,
  numberOfSims,
  orderType,
  isUlfp = false,
}: {
  id: string | null
  orderCredit?: OrderCredit
  plan: Plan
  checkoutStep?: number
  checkoutType: DATA_LAYER_JOURNEY_TYPES
  portInOption?: PORT_IN_STATUS
  membersNumber?: number
  numberOfSims?: number
  orderType?: ORDER_TYPES
  isUlfp: boolean
}): TransactionData => {
  const total =
    membersNumber && membersNumber > 1 && plan?.additionalSims
      ? getFamilyPlanPrice(plan, membersNumber)
      : plan?.finalPriceWithoutCredit?.value

  return {
    ...(id && { id }),
    'lead-generator': orderCredit?.lead_generator || null,
    ...(portInOption && { selected_port_in_option: portInOption }),
    payment: orderCredit ? buildPaymentData(orderCredit) : undefined,
    cart: {
      checkoutType,
      ...(checkoutStep && { checkoutStep }),
      ...(orderType && { order_type: orderType }),
      number_of_sims: numberOfSims || 1,
      lines: [
        {
          product: {
            ...buildV2ProductData({ plan, isUlfp, total }),
            currency: 'GBP',
            quantity: 1,
          },
        },
      ],
      value: {
        chargeType: 'monthly',
        displayGross: total,
        displayTax: returnV2CalculatedTax(total),
      },
    },
  }
}

export const buildAddonTransactionData = (
  id: string,
  orderCredit: OrderCredit,
  addon: Bolton
): AddonTransactionData => {
  const { price_to_be_paid } = addon
  return {
    transaction: {
      id,
      payment: buildPaymentData(orderCredit),
      cart: {
        checkoutType: DATA_LAYER_JOURNEY_TYPES.CHECKOUT_ADDON,
        lines: [
          {
            product: buildAddonProductData(addon),
          },
        ],
        value: {
          displayGross: returnCalculatedGross(price_to_be_paid).toFixed(2),
          displayTax: returnCalculatedTax(price_to_be_paid).toFixed(2),
        },
      },
    },
    user: buildUser() as UserObject,
  }
}

export const buildMarketingPrefsString = (
  dmpgMarketingPrefsAttrs = {}
): string =>
  Object.entries(dmpgMarketingPrefsAttrs)
    .map(([key, value]) => `${key}: ${Boolean(value)}`)
    .join('|')
